// YouTube publish client.
//
// Calls the backend to publish a certified catch clip to THE 99's YouTube
// channel. The upload itself happens server-side (the bytes live in a private
// R2 bucket and the channel OAuth token never touches the device). Auth is
// enforced server-side by verifying the caller's Firebase ID token.
//
// Backend contract (Firebase Functions, Python HTTP):
//   POST /v1/video/publish-youtube
//     body: { certId }
//     resp (ok):       { status: "ok", url, videoId }
//     resp (existing): { status: "already", url, videoId }
//     resp (error):    { status: "error", error: { code, message } }
// Sends Authorization: Bearer <Firebase ID token>.

import { auth } from "../firebase";
import { resolveApiBaseUrl } from "./apiBase";

async function getIdToken() {
  const user = auth?.currentUser;
  if (!user) throw new Error("Not signed in — cannot publish to YouTube");
  return user.getIdToken();
}

/**
 * Publish a certified catch clip to THE 99's YouTube channel.
 *
 * @param {string} certId - The certificate/video id (videos/{certId}).
 * @returns {Promise<{ status: string, url: string, videoId: string }>}
 */
export async function publishCatchToYouTube(certId) {
  if (!certId) throw new Error("certId is required");
  const baseUrl = resolveApiBaseUrl();
  const token = await getIdToken();
  const res = await fetch(`${baseUrl}/v1/video/publish-youtube`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ certId }),
  });

  let data = null;
  try {
    data = await res.json();
  } catch (_) {
    /* non-JSON error body */
  }

  if (!res.ok || (data && data.status === "error")) {
    const message =
      (data && data.error && data.error.message) ||
      (data && data.message) ||
      `YouTube publish failed (${res.status})`;
    throw new Error(message);
  }

  return {
    status: (data && data.status) || "ok",
    url: (data && data.url) || "",
    videoId: (data && data.videoId) || "",
  };
}
